// components/create/YouTubePlaylistPicker.js
//
// Sign in to YouTube, pick a playlist or make one, and put songs in it —
// the web half of the app's YouTubePlaylistSelector.
//
// ── What it does that pasting a link cannot ────────────────────────────────
//
// A pasted link is taken on trust: the host cannot tell from it whether the
// playlist is public, and the failure surfaces to their guests rather than to
// them. Signed in, the list comes from their own account with each playlist's
// privacy on it, a new playlist is created public, and a private one can be
// fixed in place.
//
// ── Adding songs by link, not by search ────────────────────────────────────
//
// Search needs an API key in the bundle. The app ships one, and that is a
// choice the app can make because its key is restricted to its bundle ids; a
// key in a website's JavaScript is readable by anyone and restricted only by
// HTTP referrer, which is trivially forged. So songs are added by pasting a
// video link — the id comes out of the URL and goes in through the same
// authorised request as everything else here, no key required.

import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { FaYoutube } from 'react-icons/fa';
import {
  isConfigured,
  isGisReady,
  loadGis,
  isSignedIn,
  signIn,
  signOut,
  getMyPlaylists,
  createPlaylist,
  addVideoToPlaylist,
  getPlaylistItems,
  makePlaylistPublic,
} from '../../services/youtubeService';
import { getYouTubeVideoId } from '../../utils/youtubeUtils';
import '../../styles/YouTubePlaylistPicker.scss';
import Icon from '../Icon';
import Portal from '../Portal';

export default function YouTubePlaylistPicker({ onPick, onClose }) {
  const [signedIn, setSignedIn] = useState(isSignedIn());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [playlists, setPlaylists] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const [videoUrl, setVideoUrl] = useState('');
  const [adding, setAdding] = useState(false);

  // Fetch Google's script the moment this opens, not when the button is
  // pressed. A popup may only open while the browser is still handling the
  // click, and awaiting a script load ends that window — see signIn.
  const [gisReady, setGisReady] = useState(isGisReady());
  useEffect(() => {
    if (!isConfigured() || gisReady) return;
    loadGis().then(() => setGisReady(true)).catch(() => {});
  }, [gisReady]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setPlaylists(await getMyPlaylists());
    } catch (err) {
      setError(err.message);
      if (!isSignedIn()) setSignedIn(false);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (signedIn) load();
  }, [signedIn, load]);

  const handleSignIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await signIn();
      setSignedIn(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const openPlaylist = async (playlist) => {
    setSelected(playlist);
    setItems([]);
    try {
      setItems(await getPlaylistItems(playlist.id));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    setError(null);
    try {
      const playlist = await createPlaylist(title, 'Created with Invits');
      setPlaylists((prev) => [playlist, ...prev]);
      setShowCreate(false);
      setNewTitle('');
      await openPlaylist(playlist);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleAddVideo = async () => {
    const videoId = getYouTubeVideoId(videoUrl);
    if (!videoId) {
      setError('That is not a YouTube video link.');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      await addVideoToPlaylist(selected.id, videoId);
      setVideoUrl('');
      setItems(await getPlaylistItems(selected.id));
      setPlaylists((prev) =>
        prev.map((p) => (p.id === selected.id ? { ...p, itemCount: p.itemCount + 1 } : p)),
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleMakePublic = async () => {
    setBusy(true);
    setError(null);
    try {
      await makePlaylistPublic(selected.id, selected.title);
      const updated = { ...selected, privacyStatus: 'public' };
      setSelected(updated);
      setPlaylists((prev) => prev.map((p) => (p.id === selected.id ? updated : p)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const use = () => {
    onPick(`https://www.youtube.com/playlist?list=${selected.id}`);
    onClose();
  };

  return (
    <Portal>
    <div className="ytp-scrim" onClick={onClose}>
      <div
        className="ytp-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="YouTube playlists"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ytp-head">
          <h2>
            <FaYoutube style={{ color: '#FF0000' }} />
            {selected ? selected.title : 'Your playlists'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>

        <div className="ytp-body">
          {error && <p className="ytp-error">{error}</p>}

          {!isConfigured() ? (
            <p className="ytp-note">
              Signing in to YouTube is not set up on this site yet. You can still paste
              a playlist link, which works just as well — it just cannot tell you
              whether the playlist is public.
            </p>
          ) : !signedIn ? (
            <div className="ytp-signin">
              <p>
                Sign in to see the playlists on your account, make a new one, and add
                songs to it without leaving this page.
              </p>
              <button
                type="button"
                className="ytp-primary"
                onClick={handleSignIn}
                disabled={busy || !gisReady}
              >
                {busy ? 'Opening Google…' : gisReady ? 'Sign in with Google' : 'Preparing…'}
              </button>
              <small>
                Invits asks only to manage your YouTube playlists, and keeps the
                sign-in for this page only — never saved, never sent anywhere.
              </small>
            </div>
          ) : selected ? (
            <>
              {selected.privacyStatus !== 'public' && (
                <div className="ytp-warn">
                  <p>
                    This playlist is <strong>{selected.privacyStatus}</strong>. Guests
                    cannot play it — they would get an error on your invitation.
                  </p>
                  <button type="button" className="ytp-secondary" onClick={handleMakePublic} disabled={busy}>
                    Make it public
                  </button>
                </div>
              )}

              <div className="ytp-add">
                <label htmlFor="ytp-video">Add a song by link</label>
                <div className="ytp-add-row">
                  <input
                    id="ytp-video"
                    type="url"
                    inputMode="url"
                    placeholder="https://www.youtube.com/watch?v=…"
                    value={videoUrl}
                    onChange={(e) => { setVideoUrl(e.target.value); setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddVideo(); }}
                  />
                  <button
                    type="button"
                    className="ytp-icon-btn"
                    onClick={handleAddVideo}
                    disabled={adding || !videoUrl.trim()}
                    aria-label="Add to playlist"
                  >
                    <FiPlus />
                  </button>
                </div>
              </div>

              <ul className="ytp-items">
                {items.length === 0 ? (
                  <li className="ytp-empty">Nothing in this playlist yet.</li>
                ) : (
                  items.map((item) => (
                    <li key={item.id}>
                      {item.thumbnail && <img src={item.thumbnail} alt="" loading="lazy" />}
                      <span>{item.title}</span>
                    </li>
                  ))
                )}
              </ul>

              <div className="ytp-actions">
                <button type="button" className="ytp-secondary" onClick={() => setSelected(null)}>
                  Back
                </button>
                <button type="button" className="ytp-primary" onClick={use}>
                  Use this playlist
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="ytp-toolbar">
                <button type="button" className="ytp-secondary" onClick={() => setShowCreate((v) => !v)}>
                  <Icon name="add" size={15} />
                  New playlist
                </button>
                <button type="button" className="ytp-icon-btn" onClick={load} disabled={busy} aria-label="Refresh">
                  <FiRefreshCw className={busy ? 'is-spinning' : ''} />
                </button>
              </div>

              {showCreate && (
                <div className="ytp-create">
                  <input
                    type="text"
                    placeholder="Playlist name"
                    value={newTitle}
                    maxLength={100}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                    autoFocus
                  />
                  <button
                    type="button"
                    className="ytp-primary"
                    onClick={handleCreate}
                    disabled={busy || !newTitle.trim()}
                  >
                    Create
                  </button>
                  {/* Said plainly, because it is the whole reason this is
                      better than pasting a link. */}
                  <small>Made public, so your guests can actually play it.</small>
                </div>
              )}

              {busy && playlists.length === 0 ? (
                <p className="ytp-note">Loading your playlists…</p>
              ) : playlists.length === 0 ? (
                <p className="ytp-note">
                  No playlists on this account yet. Make one above.
                </p>
              ) : (
                <ul className="ytp-list">
                  {playlists.map((playlist) => (
                    <li key={playlist.id}>
                      <button type="button" onClick={() => openPlaylist(playlist)}>
                        {playlist.thumbnail ? (
                          <img src={playlist.thumbnail} alt="" loading="lazy" />
                        ) : (
                          <span className="ytp-thumb-empty" aria-hidden="true"><FaYoutube /></span>
                        )}
                        <span className="ytp-list-text">
                          <strong>{playlist.title}</strong>
                          <small>
                            {playlist.itemCount} {playlist.itemCount === 1 ? 'video' : 'videos'}
                            {playlist.privacyStatus !== 'public' && ` · ${playlist.privacyStatus}`}
                          </small>
                        </span>
                        <Icon name="chevron-forward" size={18} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="button"
                className="ytp-signout"
                onClick={() => { signOut(); setSignedIn(false); setPlaylists([]); }}
              >
                Sign out of YouTube
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </Portal>
  );
}
